/**
 * Categories list — CRUD inline.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Plus, Tag } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { categoriesApi, type Category } from "@/lib/api-cadastros";

export function CategoriesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Partial<Category>>({ active: true });

  const list = useQuery({ queryKey: ["categories"], queryFn: () => categoriesApi.list({ size: 200 }) });

  const create = useMutation({
    mutationFn: (body: Partial<Category>) => categoriesApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Categoria criada");
      setOpen(false);
      setForm({ active: true });
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const columns: ColumnDef<Category>[] = [
    {
      id: "name",
      accessorKey: "name",
      header: "Categoria",
      cell: ({ row }) => (
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-500/10">
            <Tag className="h-3.5 w-3.5" />
          </div>
          <span className="font-medium">{row.original.name}</span>
        </div>
      ),
    },
    {
      id: "slug",
      accessorKey: "slug",
      header: "Slug",
      cell: ({ row }) => (
        <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/5 text-xs font-mono">
          {row.original.slug}
        </code>
      ),
    },
    {
      id: "description",
      accessorKey: "description",
      header: "Descrição",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground truncate max-w-md">
          {row.original.description ?? "—"}
        </span>
      ),
    },
    {
      id: "active",
      accessorKey: "active",
      header: "Status",
      cell: ({ row }) =>
        row.original.active ? (
          <Badge variant="success" dot>Ativa</Badge>
        ) : (
          <Badge variant="muted" dot>Inativa</Badge>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Categorias"
        description="Skills e categorias de serviço técnico."
        actions={
          <Button onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" /> Nova categoria
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isLoading}
        searchPlaceholder="Buscar categoria..."
        emptyTitle="Nenhuma categoria"
        emptyDescription="Cadastre categorias pra organizar atendimentos."
        onRowClick={(c) => navigate(`/categories/${c.id}`)}
        columnLabels={{
          name: "Categoria",
          slug: "Slug",
          description: "Descrição",
          active: "Status",
        }}
      />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Nova categoria</SheetTitle>
            <SheetDescription>Skill de atendimento técnico.</SheetDescription>
          </SheetHeader>
          <SheetBody className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Nome *</Label>
              <Input
                value={form.name ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    name: e.target.value,
                    slug: e.target.value
                      .toLowerCase()
                      .normalize("NFD")
                      .replace(/[̀-ͯ]/g, "")
                      .replace(/[^a-z0-9]+/g, "-")
                      .replace(/(^-|-$)/g, ""),
                  })
                }
                placeholder="Ex: Suporte Windows"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Slug *</Label>
              <Input
                value={form.slug ?? ""}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="font-mono"
                placeholder="suporte-windows"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Descrição</Label>
              <Textarea
                rows={3}
                value={form.description ?? ""}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild>
              <Button variant="outline">Cancelar</Button>
            </SheetClose>
            <Button
              onClick={() => create.mutate(form)}
              disabled={create.isPending || !form.name || !form.slug}
            >
              {create.isPending ? "Criando..." : "Criar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
